/**
 * EbonyMaw.js
 * The Ebony Maw Enemy class
 */

import { Game } from "pixi_core";
import { EnemyAbilities, GameEvents } from "../Constants";
import { Enemy } from "./Enemy";
import { EnemyParticle } from "../game/particles/EnemyParticle";
import { Common } from "../game/Common";

export class EbonyMaw extends Enemy {
    /**
     * @public
     * @param {String} enemyName
     * @param {Object} enemyData
     * @param {Number} index
     * @param {Boolean} [summoned] - Summoned by an ability
     */
    constructor(enemyName, enemyData, index, summoned) {
        super(enemyName, enemyData, index, summoned);

        const probTelekinesis = Game.utils.assertNumber(enemyData.ProbTelekinesis, 1);
        const probLevitation = Game.utils.assertNumber(enemyData.ProbAddOvertime, 1);
        const probExpertTactician = Game.utils.assertNumber(enemyData.ProbExpertTactician, 1);

        /** @private @type {Boolean} */
        this._isCharging = false;

        // Ability choices
        this._abilityChoices.addChoice(EnemyAbilities.eTELEKINESIS, probTelekinesis);
        this._abilityChoices.addChoice(EnemyAbilities.eLEVITATION, probLevitation);
        this._abilityChoices.addChoice(EnemyAbilities.eEXPERT_TACTICIAN, probExpertTactician);
    }

    /**
     * @protected
     * @override
     * @param {EnemyAbilities} abilityName
     * @param {{x: Number, y: Number}} startPos
     */
    _useAbility(abilityName, startPos) {
        switch (abilityName) {
            case EnemyAbilities.eTELEKINESIS:
                this._isCharging = true;
                Game.fire(GameEvents.eGAME_ABILITY_ENEMY_POWER_UP, this);
                this._continueTelekinesis(startPos);
                break;

            case EnemyAbilities.eLEVITATION:
                Game.fire(GameEvents.eGAME_ABILITY_ADD_OVERTIME, this._addOvertimeBubbles, this._onAbilityCompleted.bind(this));
                break;

            case EnemyAbilities.eEXPERT_TACTICIAN:
                Game.fire(
                    GameEvents.eGAME_ABILITY_RANDOM_SHOOTER,
                    this._randomShooterBubbles,
                    startPos,
                    this._onAbilityCompleted.bind(this)
                );
                break;

            default:
                console.error("[EbonyMaw._useAbility] Invalid ability name : " + abilityName);
                this._chooseNextAction();
        }
    }

    /**
     * @private
     * @param {{x: Number, y: Number}} startPos
     */
    _continueTelekinesis(startPos) {
        if (this._timerMultipleAbilityDelay) {
            this._timerMultipleAbilityDelay.destroy();
            this._timerMultipleAbilityDelay = null;
        }

        this._timerMultipleAbilityDelay = Game.windows.getCurrentWindow().createTimer(Common.settings.multipleAbilityDelay);
        this._timerMultipleAbilityDelay.onComplete = () => {
            Game.fire(GameEvents.eGAME_ABILITY_TRANSFORM_TO_STONE, this._colorChangeBubbles, startPos, this._onAbilityCompleted.bind(this));
        };
    }

    /**
     * Choose the next action in this enemy's attack phase
     * @protected
     * @override
     */
    _chooseNextAction() {
        if (this._nbActionsPerformed < this._nbActionsPerTurn && this._hp > 0) {
            ++this._nbActionsPerformed;

            if (this._currentAttackTimer > 0 || this._isCharging) {
                this._regularAttack();
            } else {
                this._beginAbility(this._chooseAbility());
            }
        } else {
            this._onAttackPhaseCompleted();
        }
    }

    /**
     * The attack particle has reached its target
     * @protected
     * @override
     * @param {EnemyParticle} particle
     */
    _onDamageParticleCompleted(particle) {
        if (this._isCharging) {
            const chargedDamage = this._attackDamage * (this._chargingDamageBonus / 100);
            Game.fire(GameEvents.eHEROES_HIT, chargedDamage, particle);
            Game.fire(GameEvents.eGAME_ABILITY_ENEMY_POWER_DOWN, this);
            this._isCharging = false;
        } else {
            Game.fire(GameEvents.eHEROES_HIT, this._attackDamage, particle);
        }

        this._chooseNextAction();
    }
}
